package co.maytes.standupbot.dashboard.features.settings

import co.maytes.standupbot.shared.billing.PlanCatalog
import co.maytes.standupbot.shared.billing.PlanSpec
import co.maytes.standupbot.shared.dto.billing.BillingIntervalDto
import co.maytes.standupbot.shared.dto.billing.BillingStatusDto
import co.maytes.standupbot.shared.dto.billing.PlanDto
import co.maytes.standupbot.shared.dto.billing.PlanSourceCodes
import co.maytes.standupbot.shared.dto.billing.SubscriptionStatusDto

data class BillingSubtitle(val lead: String, val rest: String)

object BillingCopy {
    const val UPGRADE_HINT = "Annual saves about 20%."
    const val SALES_NOTE = "Need more than Pro? Email sales@standupbot.io for Business and Enterprise."
    const val PORTAL_DESCRIPTION =
        "Update your card, change billing interval, download invoices, or cancel. Opens Stripe's hosted billing portal."
    const val NOT_ENABLED_DESCRIPTION = "This workspace is running without a payment provider."
    const val NOT_ENABLED_BODY = "There is nothing to upgrade or manage here."
    const val NO_BILLING_PERIOD = "No active billing period"

    val upgradeHighlights: List<String> = listOf(
        "Unlimited members & standups",
        "1-year history",
        "Audit log",
    )

    private const val ISO_DATE_CHARS = 10

    fun planLabel(plan: PlanDto): String = when (plan) {
        PlanDto.FREE -> "Free"
        PlanDto.PRO -> "Pro"
        PlanDto.BUSINESS -> "Business"
    }

    fun tagline(plan: PlanDto): String = spec(plan).summary

    fun memberCap(plan: PlanDto): Int? = spec(plan).memberCap

    fun memberFraction(plan: PlanDto, count: Int): Double? =
        memberCap(plan)?.takeIf { it > 0 }?.let { (count.toDouble() / it).coerceIn(0.0, 1.0) }

    fun memberHeadroom(plan: PlanDto, count: Int): String {
        val cap = memberCap(plan) ?: return "No member limit on ${planLabel(plan)}"
        return when {
            count > cap -> "Over the member limit"
            count == cap -> "At the member limit"
            else -> "Room for ${cap - count} more"
        }
    }

    fun memberSentence(plan: PlanDto, count: Int): String {
        val noun = if (count == 1 && memberCap(plan) == null) "member" else "members"
        return memberCap(plan)?.let { "$count of $it members" } ?: "$count $noun"
    }

    fun renewalLabel(status: BillingStatusDto): String = when {
        status.currentPeriodEnd == null -> "Renewal"
        status.cancelAtPeriodEnd -> "Cancels"
        else -> "Renews"
    }

    fun renewalDate(status: BillingStatusDto): String? = status.currentPeriodEnd?.take(ISO_DATE_CHARS)

    fun renewalNote(status: BillingStatusDto): String? = if (status.currentPeriodEnd == null) NO_BILLING_PERIOD else null

    fun subtitle(status: BillingStatusDto): BillingSubtitle {
        val members = memberSentence(status.plan, status.memberCount)
        val tail = when {
            !status.billingEnabled -> " Billing is not enabled on this workspace."
            status.currentPeriodEnd != null ->
                " ${renewalLabel(status)} ${renewalDate(status)}."
            memberCap(status.plan)?.let { status.memberCount >= it } == true -> " Upgrade to remove the limit."
            else -> ""
        }
        return BillingSubtitle(lead = "${planLabel(status.plan)} plan", rest = ", $members.$tail")
    }

    fun priceLine(interval: BillingIntervalDto): String = when (interval) {
        BillingIntervalDto.ANNUAL -> "$${PlanCatalog.pro.annualMonthlyPriceUsd} /mo, billed annually"
        BillingIntervalDto.MONTHLY -> "$${PlanCatalog.pro.monthlyPriceUsd} /mo"
    }

    fun planSourceNote(planSource: String): String? = when (planSource) {
        PlanSourceCodes.PAID_GRACE -> "Your last payment failed. Update your payment method to keep this plan."
        PlanSourceCodes.DOWNGRADED -> "Your subscription is no longer active, so this workspace is on Free."
        else -> null
    }

    fun statusLabel(status: SubscriptionStatusDto): String = when (status) {
        SubscriptionStatusDto.ACTIVE -> "Active"
        SubscriptionStatusDto.TRIALING -> "Trial"
        SubscriptionStatusDto.PAST_DUE -> "Past due"
        SubscriptionStatusDto.CANCELED -> "Canceled"
        SubscriptionStatusDto.UNPAID -> "Unpaid"
        SubscriptionStatusDto.INCOMPLETE, SubscriptionStatusDto.INCOMPLETE_EXPIRED -> "Incomplete"
    }

    private fun spec(plan: PlanDto): PlanSpec = when (plan) {
        PlanDto.FREE -> PlanCatalog.free
        PlanDto.PRO -> PlanCatalog.pro
        PlanDto.BUSINESS -> PlanCatalog.business
    }
}
